// src/Category.js
import React, { useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import useFetchMenu, { FEATURED_CATEGORY_ID } from '../../Hooks/UseFetchMenu';
import MenuCard from '../Card/Card';
import WineFlipCard from '../WineFlipCard/WineFlipCard';
import SmallNavBar from '../SmallNavBar/SmallNavBar';
import MidnightCategory from '../MidnightCategory/MidnightCategory';
import WineFlavorMap from '../WineFlavorMap/WineFlavorMap';
import styles from './Category.module.css';
import { API_BASE_URL } from '../../config';

const Category = () => {
  const { id } = useParams();
  const location = useLocation();
  const { menuItems, loading, error } = useFetchMenu(API_BASE_URL);
  const titleRef = useRef(null);
  const openWineId = location.state?.wineId;

  if (loading) {
    return <div></div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  // Los ids de categoria de SumUp son UUIDs (string): comparar como string,
  // no parsear con parseInt (rompia siempre con NaN).
  const categoryId = id;
  const category = menuItems.find((cat) => String(cat.id) === categoryId);

  if (!category) {
    return <div>Categoría no encontrada</div>;
  }

  // Para la categoría de destacados, no separar en premium/regular
  const isFeaturedCategory = categoryId === String(FEATURED_CATEGORY_ID);

  // Verificar si es una categoría con estética midnight xerox
  const categoryNameLower = (category?.name ?? '').toLowerCase();

  // Verificar si es la categoría de vinos naturales (por nombre: el id
  // numerico viejo de Tiller ya no existe, y Loyverse la nombro distinto
  // a SumUp - "Vino Natural" en singular en vez de "Vinos Naturales").
  const isNaturalWinesCategory =
    categoryNameLower.includes('vinos naturales') || categoryNameLower.includes('vino natural');

  // Vinos naturales usa el mapa de sabores como vista principal (ya no una
  // pestaña aparte accesible por link): al entrar a la categoria se ve
  // directamente el carrusel, no la lista clasica de tarjetas.
  if (isNaturalWinesCategory) {
    return <WineFlavorMap />;
  }

  // Se compara sin tildes y por raiz: los nombres de SumUp no coinciden con
  // los viejos ("Copa de vino" vs "vinos por copa", "Vino Orgánico" vs
  // "vinos organicos", "De autor"), y esas categorias caian en el estilo
  // claro/plano en vez del oscuro del resto del menu.
  const categoryNameBase = categoryNameLower.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const midnightCategories = [
    'tapa', 'cocktail', 'mocktail', 'frozen', 'organico', 'copa', 'de autor',
    'bebida', 'zumo', 'cerveza', 'cafeteria'
  ];
  const isMidnightCategory = midnightCategories.some(term =>
    categoryNameBase.includes(term)
  );

  // Categorías de cócteles: tienen su propio mapa de sabores (dulzor/fuerza)
  const cocktailCategoryTerms = ['cocktails', 'nuestros cocktails', 'mocktails', 'tragos frozen'];
  const isCocktailCategory = cocktailCategoryTerms.some(term =>
    categoryNameLower.includes(term.toLowerCase())
  );

  // Separar productos premium y regulares para mejor presentación (no para destacados)
  const premiumProducts = isFeaturedCategory 
    ? [] 
    : category.products.filter(p => (p.price / 100) > 15 || p.isSignature);
  const regularProducts = isFeaturedCategory
    ? category.products
    : category.products.filter(p => (p.price / 100) <= 15 && !p.isSignature);
  
  return (
    <div className={`${isMidnightCategory ? '' : 'backgroundApp'} ${isNaturalWinesCategory && !isMidnightCategory ? styles.darkBackground : ''}`}>
      <div className='headerNavigator'>
        <Link to="/Home">Volver al menú</Link>
        <Link to="/AudioCulture">
          Audio Culture
        </Link>
        <Link to={"https://instagram.com/bliss.ruzafa"} target="_blank" rel="noopener noreferrer">
          Keep in Touch!
        </Link>
      </div>
      <SmallNavBar />
      
      {/* Mostrar destacados si hay productos destacados en esta categoría (excepto en la propia categoría de destacados) */}
      {/* Oculto temporalmente
      {categoryFeatured.length > 0 && showFeatured && !isFeaturedCategory && (
        <div style={{ marginBottom: '20px' }}>
          <FeaturedProducts products={categoryFeatured} />
        </div>
      )}
      */}

      {isMidnightCategory ? (
        <>
          {isCocktailCategory && (
            <div className={styles.cocktailMapBar}>
              <Link to={`/Category/${category.id}/cocteles`} className={styles.mapLink}>
                Ver mapa de sabores
              </Link>
            </div>
          )}
          <MidnightCategory category={category} showHeader={false} />
        </>
      ) : (
        <>
          {isNaturalWinesCategory ? (
            <div ref={titleRef} className={styles.brutalistTitle}>
              <h2 className={styles.categoryTitleBrutalist}>
                {(() => {
                  const words = category.name.split(' ');
                  if (words.length > 1) {
                    const lastWord = words[words.length - 1];
                    const restWords = words.slice(0, -1).join(' ');
                    return (
                      <>
                        {restWords}
                        <br/>
                        <span className={styles.titleHighlight}>{lastWord}</span>
                      </>
                    );
                  }
                  return <span className={styles.titleHighlight}>{category.name}</span>;
                })()}
              </h2>
              <div className={styles.titleUnderline}></div>
              <div className={styles.titleAccent}></div>
              <Link to={`/Category/${category.id}/mapa`} className={styles.mapLink}>
                Ver mapa de sabores
              </Link>
            </div>
          ) : (
            <h2 className='CategoryTitle'>{category.name}</h2>
          )}
          <div className={`ProductsByCategory ${isNaturalWinesCategory ? 'wineGrid' : ''}`}>
            {isNaturalWinesCategory ? (
              category.products.map((item) => (
                <WineFlipCard 
                  key={item.id} 
                  item={item} 
                  categoryId={category.id}
                  categoryName={category.name}
                  initialOpen={openWineId != null && String(openWineId) === String(item.id)}
                />
              ))
            ) : (
              <>
                {premiumProducts.length > 0 && premiumProducts.map((item) => (
                  <MenuCard 
                    key={item.id} 
                    item={item} 
                    categoryId={category.id}
                    categoryName={category.name}
                  />
                ))}
                {regularProducts.map((item) => (
                  <MenuCard 
                    key={item.id} 
                    item={item} 
                    categoryId={category.id}
                    categoryName={category.name}
                  />
                ))}
              </>
            )}
            {isNaturalWinesCategory ? (
              <button className='FlechaHome' onClick={() => titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                ↑
              </button>
            ) : (
              <Link to={"/Home"} className='FlechaHome'>←</Link>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Category;
